import api from "./index.ts";
import { getExceptionMessage } from "../exceptions/helpers/getExceptionMessage.ts";

/**
 * MB-379 — cliente del endpoint de renovación de expediente.
 *
 * Vive aislado del resto de la capa de API porque la renovación es la única
 * operación del expediente que crea OTRO expediente: su respuesta no actualiza
 * el folio que estamos viendo, se usa para navegar fuera de él. Mezclarla con
 * los thunks de `folderActions` haría que el estado de Redux del folio actual
 * se contaminara con el folio nuevo.
 *
 * Todo lo que depende de la forma exacta del endpoint —la ruta, el cuerpo y la
 * traducción de sus errores a algo mostrable— está aquí y en ningún otro
 * archivo: si el contrato se mueve, se mueve en un solo sitio.
 *
 * Contrato real (apps/docvault/renewals/views.py, apps/docvault/renewals/serializers.py):
 * POST /docvault/folders/{uuid}/renew, 201 con el folio nuevo, 4xx con
 * `{ code, message, details }`, 409 cuando ya existe la renovación.
 */

interface CreateRenewalParams {
  folderId: string;
}

/**
 * Lo que devuelve una renovación exitosa.
 *
 * Las tres últimas claves no son telemetría: son la diferencia entre "listo" y
 * "listo salvo estas cosas". El expediente nuevo nace en revisión, y lo que la
 * proyección no pudo traer es justo lo que el revisor tiene que buscar a mano.
 * Tirarlas aquí sería prometer un expediente completo.
 *
 * Los nombres del payload (`subfolder_slug`, `pending_upload_reason`) se
 * traducen a camelCase aquí y en ningún otro sitio: el componente no debería
 * tener que conocer la forma en que el proyector nombra sus propias cosas. Los
 * CÓDIGOS de motivo, en cambio, viajan CRUDOS: su traducción al castellano es
 * texto de pantalla, no forma del contrato, y vive en `REASON_LABELS` dentro de
 * `utils/folders/renewalSummaryLines.ts`.
 */
export interface RenewalResult {
  id: string;
  folderNumber: number | string;
  filesCopied: number;
  requirementsPendingUpload: RenewalPendingRequirement[];
  skippedSubfolders: RenewalSkippedSubfolder[];
}

/** Un requisito del folio nuevo que nació sin documento, y por qué. */
export interface RenewalPendingRequirement {
  subfolderSlug: string;
  /**
   * Dos personas del mismo rol comparten `subfolderSlug`: el id de su
   * subcarpeta en el folio nuevo es lo único que dice de quién es el pendiente.
   */
  subfolderId: string;
  requirementSlug: string;
  reason: string;
}

/** Un participante que la proyección dejó fuera a propósito, y por qué. */
export interface RenewalSkippedSubfolder {
  slug: string;
  reason: string;
}

/**
 * Las cinco maneras en que esto puede fallar, que no se presentan igual:
 *
 * - `already_exists` (409) no es un error de quien pulsa, es el estado real del
 *   mundo: ya hay una renovación. Lleva a dónde está en vez de invitar a
 *   reintentar. Es también el caso del doble clic, que el backend serializa con
 *   un candado sobre el folio de origen.
 * - `not_renewable` (400) y `forbidden` (401/403). Sólo los códigos
 *   `RENEWAL_*` traen un mensaje que el proyector redactó en español para
 *   mostrarse tal cual. El resto llega en inglés —el `F000` de "Folder not
 *   found.", el `detail` de los permisos de DRF— y se traduce por su código con
 *   el catálogo de excepciones, o se sustituye por un texto en español.
 * - `bug` (5xx) es una invariante rota de la proyección, que el backend decide
 *   NO disfrazar de 4xx. Su detalle habla de plantillas y subcarpetas internas
 *   y no significa nada para quien lo lee — peor, parece que el expediente
 *   estaba mal. Se sustituye por un aviso a soporte.
 * - `network` es la red caída: no hay `response` que leer, y el expediente pudo
 *   haberse creado igual al otro lado.
 */
type RenewalFailureKind =
  | "already_exists"
  | "not_renewable"
  | "forbidden"
  | "bug"
  | "network";

/** El backend ya renovó este folio: el botón no debía estar visible. */
export const RENEWAL_ALREADY_EXISTS = "RENEWAL_ALREADY_EXISTS";

const BUG_MESSAGE =
  "No se pudo crear la renovación por un problema interno. El expediente de " +
  "origen no se modificó. Avisa a soporte con el número de folio.";

const FORBIDDEN_MESSAGE = "No tienes permisos para renovar este expediente.";

const SESSION_MESSAGE =
  "Tu sesión no es válida o expiró. Vuelve a iniciar sesión e inténtalo de nuevo.";

/**
 * El prefijo de los códigos del proyector (`RenewalNotPossible`). Son los
 * únicos cuyo `message` está escrito en español para la pantalla.
 */
const RENEWAL_CODE_PREFIX = "RENEWAL_";

const NETWORK_MESSAGE =
  "No se pudo contactar al servidor para crear la renovación. Revisa tu " +
  "conexión y vuelve a abrir el expediente antes de reintentar: si la " +
  "petición alcanzó al servidor, la renovación pudo haberse creado.";

/**
 * Error con el mensaje ya redactado para mostrar y, cuando aplica, el folio de
 * la renovación que ya existía.
 *
 * Se materializa como clase (y no como objeto plano) para que el componente lea
 * `error.message` y `error.kind` sin volver a pelearse con las formas de
 * payload de error del backend: la clasificación ocurre una sola vez, aquí.
 */
export class RenewalError extends Error {
  kind: RenewalFailureKind;
  code?: string;
  existingFolderId?: string;
  existingFolderNumber?: number | string;

  constructor(
    message: string,
    options: {
      kind?: RenewalFailureKind;
      code?: string;
      existingFolderId?: string;
      existingFolderNumber?: number | string;
    } = {},
  ) {
    super(message);
    this.name = "RenewalError";
    this.kind = options.kind ?? "not_renewable";
    this.code = options.code;
    this.existingFolderId = options.existingFolderId;
    this.existingFolderNumber = options.existingFolderNumber;
  }
}

const asRecord = (value: unknown): Record<string, unknown> | undefined =>
  typeof value === "object" && value !== null && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : undefined;

const asText = (value: unknown): string | undefined =>
  typeof value === "string" && value.trim().length > 0 ? value : undefined;

const asCount = (value: unknown): number =>
  typeof value === "number" && Number.isFinite(value) ? value : 0;

const asFolio = (value: unknown): number | string | undefined =>
  typeof value === "number" || (typeof value === "string" && value.length > 0)
    ? value
    : undefined;

const asList = (value: unknown): Record<string, unknown>[] =>
  Array.isArray(value)
    ? value
        .map(asRecord)
        .filter((item): item is Record<string, unknown> => item !== undefined)
    : [];

/**
 * El `id` es lo único imprescindible de la respuesta: sin él no hay a dónde
 * redirigir y el expediente que SÍ se creó queda invisible. Su ausencia se
 * trata como bug y no como "no pasó nada", para que el mensaje al menos diga
 * que hay que buscarlo en la lista.
 */
const readRenewalResult = (payload: unknown): RenewalResult => {
  const data = asRecord(payload);
  const id = data?.id;

  if (typeof id !== "string" || id.length === 0) {
    throw new RenewalError(
      "El expediente de renovación se creó, pero no se pudo identificar para " +
        "abrirlo. Búscalo en la lista de expedientes.",
      { kind: "bug" },
    );
  }

  return {
    id,
    folderNumber: asFolio(data?.folder_number) ?? "",
    filesCopied: asCount(data?.files_copied),
    requirementsPendingUpload: asList(data?.requirements_pending_upload).map(
      (item) => ({
        subfolderSlug: asText(item.subfolder_slug) ?? "",
        subfolderId: asText(item.subfolder_id) ?? "",
        requirementSlug: asText(item.requirement_slug) ?? "",
        reason: asText(item.pending_upload_reason) ?? "",
      }),
    ),
    skippedSubfolders: asList(data?.skipped_subfolders).map((item) => ({
      slug: asText(item.slug) ?? "",
      reason: asText(item.reason) ?? "",
    })),
  };
};

/**
 * Traduce lo que haya rechazado axios a un `RenewalError` clasificado.
 *
 * Exportada porque la clasificación es la parte del botón que más fácil se
 * rompe y la que menos se nota al romperse: un 500 presentado como mensaje de
 * negocio parece un expediente no renovable, y el CX deja de insistir en algo
 * que sí debía funcionar.
 *
 * Dos detalles de este front obligan a no delegar en `extractErrorMessage`:
 *
 * 1. Para 401/403 el interceptor de `src/api/index.ts` reescribe
 *    `response.data.code` a `"I000"` y guarda el código real en `backendCode`.
 *    De ahí el `backendCode ?? code`: `extractErrorMessage` traduciría el
 *    código inventado por el interceptor.
 * 2. Ese mismo interceptor rechaza con un objeto plano, no con un `AxiosError`,
 *    así que la detección es por forma y nunca con `axios.isAxiosError`.
 */
export const toRenewalError = (error: unknown): RenewalError => {
  if (error instanceof RenewalError) return error;

  const response = asRecord(asRecord(error)?.response);
  const status =
    typeof response?.status === "number" ? response.status : undefined;
  const data = asRecord(response?.data);
  const details = asRecord(data?.details);

  const code = asText(data?.backendCode) ?? asText(data?.code);
  // El texto del backend sólo se muestra si es del proyector. Cualquier otro
  // código viene redactado en inglés para el log, no para el CX.
  const message = code?.startsWith(RENEWAL_CODE_PREFIX)
    ? (asText(data?.message) ?? asText(data?.detail))
    : undefined;

  if (status === undefined) {
    return new RenewalError(NETWORK_MESSAGE, { kind: "network" });
  }

  if (status >= 500) {
    return new RenewalError(BUG_MESSAGE, { kind: "bug", code });
  }

  if (status === 409 || code === RENEWAL_ALREADY_EXISTS) {
    return new RenewalError(message ?? "Este expediente ya se renovó.", {
      kind: "already_exists",
      code: code ?? RENEWAL_ALREADY_EXISTS,
      // `RenewalNotPossible` pasa sus kwargs tal cual a `details`, así que el
      // puntero al folio existente llega anidado y con el prefijo `existing_`.
      existingFolderId: asText(details?.existing_folder_id),
      existingFolderNumber: asFolio(details?.existing_folder_number),
    });
  }

  // Aquí no se consulta el catálogo: el interceptor convierte todo 401/403 en
  // `I000`, cuyo texto genérico no dice qué acción se negó.
  if (status === 401 || status === 403) {
    return new RenewalError(
      message ?? (status === 401 ? SESSION_MESSAGE : FORBIDDEN_MESSAGE),
      { kind: "forbidden", code },
    );
  }

  return new RenewalError(
    message ??
      getExceptionMessage(
        code,
        "No se pudo crear la renovación. Si vuelve a pasar, avisa a soporte " +
          "con el número de folio.",
      ),
    { kind: "not_renewable", code },
  );
};

/**
 * Crea el expediente de renovación a partir de `folderId` y devuelve el folio
 * nuevo.
 *
 * Es una petición larga (proyecta participantes, copia los archivos en el
 * almacenamiento y crea el folio dentro de una transacción), así que el timeout
 * se sube muy por encima del de una lectura normal: cortarla por el lado del
 * cliente no cancela la transacción del servidor, sólo deja al CX sin saber si
 * el expediente se creó.
 *
 * La ruta NO lleva barra final, igual que el patrón de `urls.py`: con barra,
 * `APPEND_SLASH` no puede rescatar un POST y la llamada moriría en un 404.
 */
export const createFolderRenewal = async ({
  folderId,
}: CreateRenewalParams): Promise<RenewalResult> => {
  try {
    const { data } = await api.post(
      `/docvault/folders/${folderId}/renew`,
      {},
      { timeout: 180000 },
    );
    return readRenewalResult(data);
  } catch (error) {
    throw toRenewalError(error);
  }
};
